import $t from '@Ravnur-Inc/l10n/$t';
import { Logger } from '@Ravnur-Inc/logger';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { defineComponent, PropType, ref } from 'vue';

import { messageFromError } from '../../transformers/message-from-error';
import { AuthRepository } from '../../types/AuthService';
import { Message } from '../../types/Message';
import AuthSubmitBtn from '../auth-submit-btn/auth-submit-btn';
import BaseAuthForm from '../base-auth-form/base-auth-form';
import PasswordField from '../password-field/password-field';
import Recaptcha from '../recaptcha/recaptcha.vue';

import './web-login-form.scss';

const CN = 'web-login-form';
const $logger = new Logger('WebLoginForm');

export default defineComponent({
  props: {
    repository: { type: Object as PropType<AuthRepository<unknown>>, required: true },
    onAuth: { type: Function as PropType<<T>(data: T) => void>, required: true },
    onMessage: { type: Function as PropType<(message: Message) => void>, required: true },
    reCaptchaSiteKey: { type: String, required: true },
    blockedUntilUtc: { type: String, required: true },
  },
  setup(props, ctx) {
    const username = ref('');
    const password = ref('');
    const recaptchaToken = ref('');
    const localBlockedUntilUtc = ref(props.blockedUntilUtc);

    const $v = useVuelidate(
      {
        username: { required },
        password: { required },
      },
      { username, password },
      { $autoDirty: true } as any
    );

    const recaptchaFailed = (error: any) => {
      $logger.error('Recaptcha failed', error);
      recaptchaToken.value = '';
    };
    const recaptchaError = (error: any) => {
      $logger.error('Recaptcha error', error);
      recaptchaToken.value = '';
    };

    const recaptchaVerified = (token: string) => {
      recaptchaToken.value = token;
    };

    const recaptchaExpired = () => {
      $logger.error('Recaptcha expired');
      recaptchaToken.value = '';
    };

    const formatBlockedUntilUtc = (blockedUntilUtc: string) => {
      const date = new Date(blockedUntilUtc);
      return date.toLocaleString();
    };

    const login = async () => {
      localBlockedUntilUtc.value = '';

      try {
        if (!recaptchaToken.value && props.reCaptchaSiteKey) {
          props.onMessage({ type: 'error', text: 'Please complete the reCAPTCHA verification' });
          return;
        }

        const resp = await props.repository.auth({
          username: username.value,
          password: password.value,
          ...(props.reCaptchaSiteKey ? { reCaptchaToken: recaptchaToken.value } : {}),
        });
        props.onAuth(resp);
      } catch (e: any) {
        if (e.blockedUntilUtc) {
          localBlockedUntilUtc.value = e.blockedUntilUtc;
          props.onMessage({
            text: 'Your are blocked: please try again later',
            type: 'error',
          });
        } else {
          props.onMessage(messageFromError(e));
        }
      }
    };

    return () => (
      <BaseAuthForm submit={login} v={$v.value}>
        <text-field
          class={`${CN}__field`}
          errors={$v.value.username.$errors}
          icon="user"
          placeholder={$t('login', 'username')}
          vModel={[username.value, 'value']}
        />

        <PasswordField
          class={`${CN}__field`}
          errors={$v.value.password.$errors}
          placeholder="password"
          vModel={[password.value, 'value']}
        />

        {props.reCaptchaSiteKey && (
          <Recaptcha
            class={`${CN}__recaptcha`}
            sitekey={props.reCaptchaSiteKey}
            size="normal"
            theme="light"
            onError={recaptchaError}
            onExpire={recaptchaExpired}
            onFail={recaptchaFailed}
            onVerify={recaptchaVerified}
          />
        )}

        {localBlockedUntilUtc.value && (
          <div class={`${CN}__blocked`}>
            <p>You are blocked until {formatBlockedUntilUtc(localBlockedUntilUtc.value)}</p>
          </div>
        )}

        <div class={`${CN}__actions`}>
          <AuthSubmitBtn ref="button" label="login" />
          {ctx.slots.actions?.()}
        </div>
      </BaseAuthForm>
    );
  },
});
